/**
 * Должности и права — §8.7 ТЗ платформы, D181, D232.
 *
 * С 25.09.2026 — одной таблицей «разделы × должности» (`Matrix.tsx`), как
 * просил владелец. Прежде должность показывалась по одной: на телефоне
 * широкая таблица не помещается, и это решено прокруткой внутри таблицы с
 * неподвижным столбцом разделов, а не отказом от таблицы.
 *
 * Денежное должностям не ставится (§8.4), «Собственник» — все права и не
 * правится: сервер отказывает сам, а экран заранее объясняет почему.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Notice } from '../../ui';
import { useMayEditTeam } from './access';
import { Link } from '../../router';
import { TEAM_KEYS, personPath, problemText, teamApi } from './api';
import { RightsMatrix } from './Matrix';
import type { PositionRights, RightsScreen } from './api';

type Result = { ok: boolean; text: string } | null;

/* ── Сортировка и отбор (просьба владельца 25.09.2026) ───────────────── */

type Order = 'usual' | 'alpha';
const ALL = '__all__';
const NONE = '__none__';

/** Подразделения, встречающиеся у должностей, — по алфавиту. */
export function departmentsOf(positions: PositionRights[]): string[] {
  const set = new Set(
    positions.map((item) => item.department?.trim()).filter((value): value is string => !!value),
  );
  return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
}

/** Должности в нужном порядке и с нужным подразделением. */
export function arrange(
  positions: PositionRights[],
  order: Order,
  department: string,
): PositionRights[] {
  const picked = positions.filter((item) => {
    if (department === ALL) return true;
    const own = item.department?.trim() || '';
    return department === NONE ? own === '' : own === department;
  });
  if (order === 'alpha') {
    return [...picked].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }
  return picked;
}

function PositionFilter({
  order,
  department,
  departments,
  onOrder,
  onDepartment,
}: {
  order: Order;
  department: string;
  departments: string[];
  onOrder: (order: Order) => void;
  onDepartment: (department: string) => void;
}) {
  const chips = [
    { value: ALL, title: 'Все' },
    ...departments.map((value) => ({ value, title: value })),
    { value: NONE, title: 'Без подразделения' },
  ];
  return (
    <section className="team-block team-block--card" aria-label="Отбор должностей">
      <div className="team-filter">
        <div className="chips" role="group" aria-label="Подразделение">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className={`chip${department === chip.value ? ' chip--active' : ''}`}
              aria-pressed={department === chip.value}
              onClick={() => onDepartment(chip.value)}
            >
              {chip.title}
            </button>
          ))}
        </div>
        <label className="field team-filter__order">
          <span className="field__label">Порядок</span>
          <select
            className="select"
            aria-label="Порядок должностей"
            value={order}
            onChange={(event) => onOrder(event.target.value as Order)}
          >
            <option value="usual">как заведены</option>
            <option value="alpha">по алфавиту</option>
          </select>
        </label>
      </div>
    </section>
  );
}

export function Positions() {
  const mayEdit = useMayEditTeam();
  const screen = useQuery<RightsScreen, ApiError>({
    queryKey: TEAM_KEYS.rights,
    queryFn: () => teamApi.rights(),
  });

  const [order, setOrder] = useState<Order>('usual');
  const [department, setDepartment] = useState<string>(ALL);

  if (screen.isPending) return <Loading text="Загружаю права…" />;
  if (screen.error) return <ErrorState error={screen.error} onRetry={() => screen.refetch()} />;

  const departments = departmentsOf(screen.data.positions);
  const shown = arrange(screen.data.positions, order, department);

  return (
    <div className="team-positions">
      <section className="team-block team-block--card">
        <h3 className="block-title">Как устроены права</h3>
        <ul className="team-rules">
          <li>Галочки ставятся должности: назначили человеку должность — он получил её права.</li>
          <li>«Видеть» — пользоваться разделом для себя; «править» — управлять им.</li>
          <li>Деньги должностям не ставятся — только поимённо, в карточке сотрудника.</li>
          <li>Поверх должности у человека бывают личные исключения — тоже в его карточке.</li>
        </ul>
      </section>

      <PositionFilter
        order={order}
        department={department}
        departments={departments}
        onOrder={setOrder}
        onDepartment={setDepartment}
      />

      <PositionList positions={shown} departments={departments} mayEdit={mayEdit} />

      <RightsMatrix screen={{ ...screen.data, positions: shown }} mayEdit={mayEdit} />

      {mayEdit && <NewPosition positions={screen.data.positions} />}
    </div>
  );
}

/* ── Список должностей: переименовать, удалить ───────────────────────── */

function PositionList({
  positions,
  departments,
  mayEdit,
}: {
  positions: PositionRights[];
  departments: string[];
  mayEdit: boolean;
}) {
  const [result, setResult] = useState<Result>(null);
  return (
    <section className="team-block team-block--card" aria-label="Список должностей">
      <h3 className="block-title">Должности</h3>
      <p className="team-hint">
        Удалить можно должность без людей. Если на неё ссылаются чек-листы или курсы, она не
        стирается, а убирается из списков — история остаётся целой.
      </p>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      <datalist id="team-departments">
        {departments.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      {positions.length === 0 ? (
        <p className="team-faint">Под выбранный отбор должностей нет.</p>
      ) : (
        <ul className="team-rows">
          {positions.map((position) => (
            <PositionRow
              key={position.id}
              position={position}
              mayEdit={mayEdit}
              onResult={setResult}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PositionRow({
  position,
  mayEdit,
  onResult,
}: {
  position: PositionRights;
  mayEdit: boolean;
  onResult: (result: Result) => void;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [asking, setAsking] = useState(false);
  const [name, setName] = useState(position.name);
  const [department, setDepartment] = useState(position.department ?? '');
  const done = () => void client.invalidateQueries({ queryKey: ['team'] });
  const rename = useMutation<{ name: string; department: string | null }, ApiError, void>({
    mutationFn: () => teamApi.renamePosition(position.id, name.trim(), department.trim()),
    onSuccess: (data) => {
      setEditing(false);
      onResult({
        ok: true,
        text: `Сохранено: «${data.name}»${data.department ? `, ${data.department}` : ''}.`,
      });
      done();
    },
    onError: (error) => onResult({ ok: false, text: problemText(error) }),
  });
  // Отметка «официант» (D247). Отдельной мутацией, а не полем формы
  // переименования: её ставят и снимают одним касанием, не открывая правку.
  const waiter = useMutation<{ is_waiter: boolean }, ApiError, boolean>({
    mutationFn: (value) => teamApi.setPositionIsWaiter(position.id, value),
    onSuccess: (data) => {
      onResult({
        ok: true,
        text: data.is_waiter
          ? `«${position.name}» теперь в разрезе официантов. Витрина показателей `
            + `пересчитается ночью — до неё цифры наставника прежние.`
          : `«${position.name}» убрана из разреза официантов: её чеки видны `
            + `отдельной строкой, но к официантам не отнесены.`,
      });
      done();
    },
    onError: (error) => onResult({ ok: false, text: problemText(error) }),
  });
  const remove = useMutation<{ result: 'deleted' | 'hidden' }, ApiError, void>({
    mutationFn: () => teamApi.deletePosition(position.id),
    onSuccess: (data) => {
      onResult({
        ok: true,
        text:
          data.result === 'deleted'
            ? `Должность «${position.name}» удалена.`
            : `Должность «${position.name}» убрана из списков: на неё ссылались чек-листы или курсы.`,
      });
      done();
    },
    onError: (error) => onResult({ ok: false, text: problemText(error) }),
  });
  const people = position.employees ?? 0;
  const [open, setOpen] = useState(false);
  const list = position.people ?? [];

  if (editing) {
    return (
      <li className="team-row">
        <form
          className="team-position-edit"
          onSubmit={(event) => {
            event.preventDefault();
            onResult(null);
            const changed =
              name.trim() !== position.name || department.trim() !== (position.department ?? '');
            if (name.trim() && changed) rename.mutate();
            else setEditing(false);
          }}
        >
          <input
            className="input"
            aria-label={`Новое название: ${position.name}`}
            value={name}
            maxLength={80}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
          <input
            className="input"
            aria-label={`Подразделение: ${position.name}`}
            placeholder="Подразделение: Зал, Кухня…"
            list="team-departments"
            value={department}
            maxLength={60}
            onChange={(event) => setDepartment(event.target.value)}
          />
          <button type="submit" className="button button--primary button--small" disabled={rename.isPending}>
            Сохранить
          </button>
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              setName(position.name);
              setDepartment(position.department ?? '');
              setEditing(false);
            }}
          >
            Отмена
          </button>
        </form>
      </li>
    );
  }

  return (
    <li className="team-row team-row--position">
      <span className="team-row__label">
        {position.name}
        <span className="team-faint">
          {position.department ? ` · ${position.department}` : ''}
          {position.is_waiter ? ' · официант' : ''}
          {' · '}
        </span>
        {list.length ? (
          <button
            type="button"
            className="team-link"
            aria-expanded={open}
            aria-label={`Кто на должности: ${position.name}`}
            onClick={() => setOpen(!open)}
          >
            {list.length} чел. {open ? '▴' : '▾'}
          </button>
        ) : (
          <span className="team-faint">
            {position.is_owner ? 'все права' : people ? `${people} чел.` : 'никого'}
          </span>
        )}
      </span>
      {mayEdit && (
        <span className="team-actions">
          {asking ? (
            <>
              <span>Удалить «{position.name}»?</span>
              <button
                type="button"
                className="button button--danger button--small"
                disabled={remove.isPending}
                onClick={() => {
                  setAsking(false);
                  onResult(null);
                  remove.mutate();
                }}
              >
                Удалить
              </button>
              <button type="button" className="button button--small" onClick={() => setAsking(false)}>
                Отмена
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="button button--small"
                aria-label={`Изменить: ${position.name}`}
                onClick={() => setEditing(true)}
              >
                Изменить
              </button>
              {/*
                Отметка «официант» — не про права, а про то, кого с кем
                сравнивать. У «Собственника» её нет: он не обслуживает
                гостей, и показывать ему галочку значило бы предлагать
                бессмысленное действие.
              */}
              {!position.is_owner && (
                <button
                  type="button"
                  className={`button button--small${position.is_waiter ? ' button--primary' : ''}`}
                  aria-label={`Официантская должность: ${position.name}`}
                  aria-pressed={Boolean(position.is_waiter)}
                  disabled={waiter.isPending}
                  onClick={() => {
                    onResult(null);
                    waiter.mutate(!position.is_waiter);
                  }}
                >
                  Официант
                </button>
              )}
              {!position.is_owner && (
                <button
                  type="button"
                  className="button button--small"
                  aria-label={`Удалить: ${position.name}`}
                  onClick={() => setAsking(true)}
                >
                  Удалить
                </button>
              )}
            </>
          )}
        </span>
      )}
      {open && list.length > 0 && (
        <ul className="team-position-people" aria-label={`Сотрудники: ${position.name}`}>
          {list.map((person) => (
            <li key={person.employee_id}>
              <Link to={personPath(person)} className="team-link">
                {person.full_name}
              </Link>
              {person.user_id === null && <span className="team-faint"> · нет Telegram</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function NewPosition({ positions }: { positions: PositionRights[] }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [result, setResult] = useState<Result>(null);
  const create = useMutation<
    { position_id: number; name: string; rights_copied: number },
    ApiError
  >({
    mutationFn: () => teamApi.createPosition(name.trim(), copyFrom ? Number(copyFrom) : null),
    onSuccess: (data) => {
      setResult({
        ok: true,
        text: data.rights_copied
          ? `Должность «${data.name}» заведена, скопировано прав: ${data.rights_copied}.`
          : `Должность «${data.name}» заведена — поставьте ей галочки в таблице выше.`,
      });
      setName('');
      setCopyFrom('');
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) =>
      setResult({
        ok: false,
        text:
          error.status === 400 && !error.detail
            ? 'Не заведена: такая должность уже есть или название пустое.'
            : problemText(error),
      }),
  });

  return (
    <form
      className="team-block team-block--card"
      onSubmit={(event) => {
        event.preventDefault();
        setResult(null);
        if (name.trim()) create.mutate();
      }}
    >
      <h3 className="block-title">Новая должность</h3>
      <div className="team-form">
        <label className="field">
          <span className="field__label">Название</span>
          <input
            className="input"
            value={name}
            maxLength={80}
            placeholder="Старший официант"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Права — как у</span>
          <select
            className="select"
            value={copyFrom}
            onChange={(event) => setCopyFrom(event.target.value)}
          >
            <option value="">начать с пустых</option>
            {positions
              .filter((item) => !item.is_owner)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <p className="team-hint">
        Права копируются один раз: дальше должности живут отдельно. Денежные не копируются.
      </p>
      <div className="team-actions">
        <button
          type="submit"
          className="button button--primary"
          disabled={!name.trim() || create.isPending}
        >
          Завести должность
        </button>
      </div>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </form>
  );
}
